/** Platform incident contacts. Generated from docs/research/dir-platforms.json (read from each platform's own site, verified 2026-11-03). Platforms with no verifiable official page are omitted; the UI never fills gaps. */
export interface PlatformEntry {
  id: string; name: string; category: string
  securityReportUrl: string | null; accountRecoveryUrl: string | null; auditLogsUrl: string | null
  supportPhone: string | null; notes: string | null; otherUrls: { label: string; url: string }[]
  needsCheck: boolean; verifiedOn: string
}
export const PLATFORMS: PlatformEntry[] = [
  {
    "id": "aws",
    "name": "Amazon Web Services (AWS)",
    "category": "cloud",
    "securityReportUrl": "https://aws.amazon.com/security/vulnerability-reporting/",
    "accountRecoveryUrl": "https://repost.aws/knowledge-center/potential-account-compromise",
    "auditLogsUrl": "https://docs.aws.amazon.com/awscloudtrail/latest/userguide/view-cloudtrail-events.html",
    "supportPhone": null,
    "notes": "Security page: abuse reports go to the AWS abuse form or trustandsafety@support.aws.com; vulnerabilities via HackerOne or aws-security@amazon.com. It says it has no specific procedure for reporting a compromised customer account; the re:Post article (title 'Resolve issues with unauthorized activity in AWS accounts') covers steps and responding in the AWS Support Center.",
    "otherUrls": [
      {
        "label": "AWS abuse form",
        "url": "https://support.aws.amazon.com/#/contacts/report-abuse"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "atlassian",
    "name": "Atlassian (Jira, Confluence, Bitbucket cloud)",
    "category": "collaboration",
    "securityReportUrl": "https://www.atlassian.com/trust/security/report-a-vulnerability",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://support.atlassian.com/security-and-access-policies/docs/export-audit-logs/",
    "supportPhone": null,
    "notes": "Customers are told to submit a ticket to Atlassian support. Audit log export: up to 10,000 activities from the past '180 days'; available on 'Any plan'; requires Organization admin, Site admin, or Unit admin role.",
    "otherUrls": [
      {
        "label": "Atlassian Support contact",
        "url": "https://support.atlassian.com/contact/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "box",
    "name": "Box",
    "category": "collaboration",
    "securityReportUrl": "https://www.box.com/trust",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://docs.box.com/en/box-admin-tools/reporting-and-insights/using-reports",
    "supportPhone": null,
    "notes": "Using Reports: 'these reports can go back as far as 7 years, or as long as you have an account tariff with reporting access'; 'You must be an organization administrator to run a report.' Trust Center names no channel for reporting an incident on your own account; it lists a vulnerability link and the Tech Support request form.",
    "otherUrls": [
      {
        "label": "Box support request form",
        "url": "https://support.box.com/hc/en-us/requests/new"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "cloudflare",
    "name": "Cloudflare",
    "category": "domains/hosting",
    "securityReportUrl": "https://developers.cloudflare.com/support/contacting-cloudflare-support/",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://developers.cloudflare.com/fundamentals/account/account-security/review-audit-logs/",
    "supportPhone": null,
    "notes": "Support doc lists account compromise as an example of a P1 (Urgent) case; Enterprise customers can call an emergency phone line but the number is not printed. Audit logs: 'retained for 18 months'; export via dashboard CSV or API.",
    "otherUrls": [
      {
        "label": "Report abuse (not for compromised accounts)",
        "url": "https://developers.cloudflare.com/fundamentals/reference/report-abuse/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "docusign",
    "name": "DocuSign",
    "category": "collaboration",
    "securityReportUrl": "https://www.docusign.com/trust/security/incident-reporting",
    "accountRecoveryUrl": null,
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "Page now points to the Docusign Safety Center (docusign.com/safety). Report impersonation email to verify@docusign.com; use Report Abuse for suspicious envelopes; Report Abuse webform for suspected fraud.",
    "otherUrls": [
      {
        "label": "Report Abuse form",
        "url": "https://www.docusign.com/safety/report-abuse-form"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "dropbox",
    "name": "Dropbox",
    "category": "collaboration",
    "securityReportUrl": null,
    "accountRecoveryUrl": "https://help.dropbox.com/security/account-hacked",
    "auditLogsUrl": "https://help.dropbox.com/account-access/view-activity",
    "supportPhone": null,
    "notes": "Hacked-account article covers version history checks, Rewind, password reset, 2-step verification and notes that a team admin may have used 'Sign in as user'. Activity: 'Usage data is available, beginning January 2017 or when your team began using Dropbox'; actions appear within 24 hours at most; CSV report export.",
    "otherUrls": [
      {
        "label": "Dropbox support",
        "url": "https://www.dropbox.com/support"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "github",
    "name": "GitHub",
    "category": "developer",
    "securityReportUrl": "https://docs.github.com/en/enterprise-cloud@latest/support/learning-about-github-support/understanding-how-github-support-can-help-during-a-security-incident",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://docs.github.com/en/organizations/keeping-your-organization-secure/managing-security-settings-for-your-organization/reviewing-the-audit-log-for-your-organization",
    "supportPhone": null,
    "notes": "Support handles security matters in writing through tickets and 'cannot fulfill requests to preserve logs or audit data'. Org audit log: 'contains data for the last 180 days'; personal security log: 'last 90 days'. Audit log streaming only captures activity from when enabled.",
    "otherUrls": [
      {
        "label": "Personal security log",
        "url": "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/reviewing-your-security-log"
      },
      {
        "label": "Emergency credential revocation",
        "url": "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/revoking-your-credentials"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "gitlab",
    "name": "GitLab",
    "category": "developer",
    "securityReportUrl": "https://about.gitlab.com/security/disclosure/",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://docs.gitlab.com/user/compliance/audit_events/",
    "supportPhone": null,
    "notes": "Disclosure page covers vulnerabilities in GitLab via HackerOne and says email to security@gitlab.com is 'no longer a supported disclosure method'. Audit events doc: 'Audit events are retained indefinitely'; group and project audit events are listed for Premium and Ultimate.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "godaddy",
    "name": "GoDaddy",
    "category": "domains/hosting",
    "securityReportUrl": "https://legalportal.godaddy.com/abuse",
    "accountRecoveryUrl": "https://www.godaddy.com/help/lock-down-a-compromised-godaddy-account-6966",
    "auditLogsUrl": "https://www.godaddy.com/help/check-my-godaddy-account-activity-32281",
    "supportPhone": null,
    "notes": "Lock-down article: change passwords, remove delegates, delete unfamiliar API keys, sign out everywhere. Activity page shows 'Activity (last 90 days)'. Locked-out owners use Account Recovery, which requires government photo ID.",
    "otherUrls": [
      {
        "label": "Regain access to a domain or account",
        "url": "https://www.godaddy.com/help/regain-access-to-my-domain-or-my-godaddy-account-4043"
      },
      {
        "label": "Contact Us form",
        "url": "https://www.godaddy.com/help/contact-us"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "google-cloud",
    "name": "Google Cloud",
    "category": "cloud",
    "securityReportUrl": "https://docs.cloud.google.com/docs/security/respond-to-abuse-misuse",
    "accountRecoveryUrl": "https://docs.cloud.google.com/docs/security/respond-to-abuse-misuse",
    "auditLogsUrl": "https://docs.cloud.google.com/logging/quotas",
    "supportPhone": "+1 (844) 613-7588",
    "notes": "Abuse page says to secure affected services and report abuse to Google Cloud Customer Care. Logging page lists the _Required bucket at '400 days' (not configurable) and _Default at '30 days'. Phone is the Mandiant incident response line printed on cloud.google.com/security/report-incident, which says its form is for corporate security incidents only.",
    "otherUrls": [
      {
        "label": "Incident Response Assistance (Mandiant)",
        "url": "https://cloud.google.com/security/report-incident"
      },
      {
        "label": "Google Cloud Customer Care",
        "url": "https://support.cloud.google.com/portal/"
      },
      {
        "label": "Cloud audit logs overview",
        "url": "https://docs.cloud.google.com/logging/docs/audit"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "google-workspace",
    "name": "Google Workspace",
    "category": "identity/email",
    "securityReportUrl": null,
    "accountRecoveryUrl": "https://knowledge.workspace.google.com/admin/support/troubleshooting/identify-and-secure-compromised-accounts",
    "auditLogsUrl": "https://knowledge.workspace.google.com/admin/reports/data-retention-and-lag-times",
    "supportPhone": null,
    "notes": "Admin checklist: suspend user, review logs, reset password, revoke OAuth tokens, remove app passwords. Data retention page lists user (login) log events and admin log events as '6 months'.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "hubspot",
    "name": "HubSpot",
    "category": "commerce/payments",
    "securityReportUrl": "https://legal.hubspot.com/security",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://knowledge.hubspot.com/account-management/view-and-export-account-activity-history",
    "supportPhone": null,
    "notes": "Audit logs need Super Admin: login history 'within the last 30 days'; security activity last 30 days with a separate export covering the last year. Security page routes vulnerabilities to Bugcrowd and lists no incident email or phone line.",
    "otherUrls": [
      {
        "label": "HubSpot Help",
        "url": "https://help.hubspot.com"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "mailchimp",
    "name": "Mailchimp",
    "category": "commerce/payments",
    "securityReportUrl": "https://mailchimp.com/help/i-think-my-account-has-been-compromised/",
    "accountRecoveryUrl": "https://mailchimp.com/help/i-think-my-account-has-been-compromised/",
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "'About Compromised Mailchimp Accounts': email Mailchimp from a secure address via the contact form; they 'turn off login and send capabilities' until you reset credentials, then delete and recreate API keys.",
    "otherUrls": [
      {
        "label": "Mailchimp contact",
        "url": "https://mailchimp.com/contact/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "meta-business",
    "name": "Meta (Facebook Pages / Business accounts)",
    "category": "other",
    "securityReportUrl": "https://www.facebook.com/help/738660629556925",
    "accountRecoveryUrl": "https://www.facebook.com/help/738660629556925",
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "Hacked Page recovery: submit the Page recovery form (log in first, or use facebook.com/hacked on a device you've used before); Meta can only act if it confirms the Page was compromised.",
    "otherUrls": [
      {
        "label": "Hacked account help",
        "url": "https://www.meta.com/help/policies/539039418231124/"
      },
      {
        "label": "Hacked",
        "url": "https://www.facebook.com/hacked"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "microsoft-365-entra",
    "name": "Microsoft 365 / Entra ID (Azure AD)",
    "category": "identity/email",
    "securityReportUrl": null,
    "accountRecoveryUrl": "https://learn.microsoft.com/en-us/defender-office-365/responding-to-a-compromised-email-account",
    "auditLogsUrl": "https://learn.microsoft.com/en-us/purview/audit-log-retention-policies",
    "supportPhone": null,
    "notes": "Microsoft's guide says to disable the account (or reset the password), revoke sessions, and review MFA devices, app consents, roles, and forwarding. Audit (Standard) logs generated on or after Oct 17, 2023 follow a 180-day default; Entra audit and sign-in logs are 'Seven days' on Free and '30 days' on P1/P2.",
    "otherUrls": [
      {
        "label": "Entra data retention",
        "url": "https://learn.microsoft.com/en-us/entra/identity/monitoring-health/reference-reports-data-retention"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "microsoft-azure",
    "name": "Microsoft Azure",
    "category": "cloud",
    "securityReportUrl": null,
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://learn.microsoft.com/en-us/azure/azure-monitor/fundamentals/activity-log",
    "supportPhone": null,
    "notes": "Activity log doc: 'Azure retains activity log events for 90 days and then deletes them.' Can be exported via diagnostic settings or CSV download.",
    "otherUrls": [
      {
        "label": "Respond to a compromised email/Entra account (shared Microsoft guide)",
        "url": "https://learn.microsoft.com/en-us/defender-office-365/responding-to-a-compromised-email-account"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "namecheap",
    "name": "Namecheap",
    "category": "domains/hosting",
    "securityReportUrl": "https://www.namecheap.com/support/knowledgebase/article.aspx/9196/5/how-and-where-can-i-file-abuse-complaints/",
    "accountRecoveryUrl": "https://www.namecheap.com/support/knowledgebase/article.aspx/10473/45/what-should-i-do-if-my-account-is-compromised/",
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "Abuse by others: abuse@namecheap.com (registered domains) or abuse@namecheaphosting.com (hosted). Compromise article: change password, enable 2FA, contact Support; account and domains may be temporarily locked during an investigation.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "okta",
    "name": "Okta",
    "category": "identity/email",
    "securityReportUrl": "https://trust.okta.com/",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://developer.okta.com/docs/api/openapi/okta-management/management/tags/systemlog",
    "supportPhone": null,
    "notes": "Trust site links 'Report a Vulnerability' and a Primary Security Contact setting used to alert customers of threats. Suspicious Activity Reporting doc: reports appear in System Log as user.account.report_suspicious_activity_by_enduser.",
    "otherUrls": [
      {
        "label": "Suspicious Activity Reporting",
        "url": "https://help.okta.com/en-us/content/topics/security/suspicious-activity-reporting.htm"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "paypal",
    "name": "PayPal",
    "category": "commerce/payments",
    "securityReportUrl": "https://www.paypal.com/us/security/report-fraud",
    "accountRecoveryUrl": "https://www.paypal.com/us/security/report-fraud",
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "Personal and Business accounts: report unauthorized transactions in the Resolution Center. Phone numbers on the page are product-specific (PayPal Credit, Cashback Mastercard, Business Debit Mastercard), so none is given as general support.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "intuit-quickbooks",
    "name": "QuickBooks / Intuit",
    "category": "commerce/payments",
    "securityReportUrl": "https://quickbooks.intuit.com/learn-support/en-us/help-article/fraud/identify-suspicious-activity-phishing-scams-fraud/L2NzZQnZu_US_en_US",
    "accountRecoveryUrl": null,
    "auditLogsUrl": null,
    "supportPhone": null,
    "notes": "Help article says to forward phishing to security@intuit.com and report through the Intuit Online Security Center (security.intuit.com/contact-us). Phone numbers on the page are sales lines only.",
    "otherUrls": [
      {
        "label": "Intuit Security Center",
        "url": "https://security.intuit.com/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "salesforce",
    "name": "Salesforce",
    "category": "commerce/payments",
    "securityReportUrl": "https://security.salesforce.com/contact/",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://help.salesforce.com/s/articleView?language=en_US&id=sf.admin_monitorsetup.htm",
    "supportPhone": null,
    "notes": "Contact Salesforce Security page offers a Report Concern form for security or data confidentiality issues. Page lists no email or phone.",
    "otherUrls": [
      {
        "label": "Login History help",
        "url": "https://help.salesforce.com/s/articleView?language=en_US&id=sf.monitoring_login_history.htm"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "shopify",
    "name": "Shopify",
    "category": "commerce/payments",
    "securityReportUrl": "https://www.shopify.com/security-response",
    "accountRecoveryUrl": "https://help.shopify.com/en/manual/privacy-and-security/account-security/account-security-best-practices",
    "auditLogsUrl": "https://help.shopify.com/en/manual/shopify-admin/activity-logs",
    "supportPhone": null,
    "notes": "Account security article: 'contact Shopify Support immediately' if hacked. Security response page: vulnerabilities via HackerOne, other concerns via support request. Store activity log keeps entries for 18 months and cannot be exported.",
    "otherUrls": [
      {
        "label": "Shopify HackerOne",
        "url": "https://hackerone.com/shopify"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "slack",
    "name": "Slack",
    "category": "collaboration",
    "securityReportUrl": "https://slack.com/security-practices",
    "accountRecoveryUrl": null,
    "auditLogsUrl": "https://slack.com/help/articles/360000394286-Audit-logs-in-Slack",
    "supportPhone": null,
    "notes": "Audit logs are Enterprise-only: 'As of April 30, 2026, audit logs are retained for a maximum of two years for all Enterprise customers.' Workspace access logs are available on paid plans and the article states no retention period.",
    "otherUrls": [
      {
        "label": "View access logs",
        "url": "https://slack.com/help/articles/360002084807-View-access-logs-for-your-workspace"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "square",
    "name": "Square",
    "category": "commerce/payments",
    "securityReportUrl": "https://squareup.com/help/us/en/article/5766-recognize-and-report-suspicious-account-activity",
    "accountRecoveryUrl": "https://squareup.com/help/us/en/article/5766-recognize-and-report-suspicious-account-activity",
    "auditLogsUrl": null,
    "supportPhone": "1 (855) 700-6000",
    "notes": "Article: contact Square immediately about unauthorized transactions or account changes; 'Only account owners and authorized representatives can contact Square about their account.' Review of account changes can take up to 48 hours.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "stripe",
    "name": "Stripe",
    "category": "commerce/payments",
    "securityReportUrl": "https://docs.stripe.com/security",
    "accountRecoveryUrl": "https://support.stripe.com/questions/unauthorized-login-attempt-notification",
    "auditLogsUrl": "https://docs.stripe.com/security",
    "supportPhone": null,
    "notes": "Security page: security history logs of sensitive activity can be exported; vulnerabilities via HackerOne; 'Send support requests from the Dashboard after you log in' so Stripe can authenticate you. Recovery article: change email and password, turn on two-step verification.",
    "otherUrls": [
      {
        "label": "Stripe support",
        "url": "https://support.stripe.com/contact/login"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "twilio",
    "name": "Twilio",
    "category": "other",
    "securityReportUrl": "https://www.twilio.com/docs/usage/fraud-response-guide",
    "accountRecoveryUrl": "https://www.twilio.com/docs/usage/fraud-response-guide",
    "auditLogsUrl": "https://www.twilio.com/docs/usage/monitor-events",
    "supportPhone": null,
    "notes": "Fraud & Abuse Response Guide directs you to Twilio Support at help.twilio.com and says not to wait for Support to start containment. Monitor Events: 13 months for Administration, Security or Enterprise Edition; 30 days for free-version customers.",
    "otherUrls": [
      {
        "label": "Twilio Support",
        "url": "https://help.twilio.com/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "wordpress-com",
    "name": "WordPress.com",
    "category": "domains/hosting",
    "securityReportUrl": "https://wordpress.com/support/security/website-hack/",
    "accountRecoveryUrl": "https://wordpress.com/support/security/website-hack/",
    "auditLogsUrl": "https://wordpress.com/support/security/website-hack/",
    "supportPhone": null,
    "notes": "Hack guide points to the activity log, Jetpack Scan, and contacting WordPress.com support. Covers only sites hosted by WordPress.com.",
    "otherUrls": [
      {
        "label": "Account recovery",
        "url": "https://wordpress.com/support/account-recovery/"
      }
    ],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  },
  {
    "id": "zoom",
    "name": "Zoom",
    "category": "collaboration",
    "securityReportUrl": "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0062911",
    "accountRecoveryUrl": "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0062911",
    "auditLogsUrl": "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0067251",
    "supportPhone": null,
    "notes": "'Reporting a compromised account': submit Zoom's Trust & Safety form and choose 'Report Account Takeover'. Admin Activity Logs live under Reports > User activity reports.",
    "otherUrls": [],
    "needsCheck": true,
    "verifiedOn": "2026-11-03"
  }
]
